import { Helmet } from "react-helmet";
import ContainerHeaderLayout from "views/components/ContainerHeaderLayout/ContainerHeaderLayout";
import styles from "./style.module.scss";
import UsersTable from "./UsersTable";
import VLButton from "views/uikit/Buttons/VLButton";
import { isOnPremUser } from "helpers/utility/utilities";
import UnavailableModal from "views/components/UnavillableModal/UnavailableModal";
import { useNavigate } from "react-router";
import UserModalManager from "./UserModalManager";
import { useState, useEffect } from "react";
import { UserData } from "./UserModalManager/ChangeUserRoleModal";
import { UserModalType } from "helpers/constants/users";
import { getUserInfo } from "redux/User/selectors";
import { useSelector } from "react-redux";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { USER_MANAGEMENT } from "helpers/constants/filters";
import VLColorfulLoader from "views/uikit/VLColorfulLoader";
import { useUserTable } from "./UsersTable/useUserTable";
import { useUserWorkspace } from "hooks/useUserWorkspace";
import { TrainWorthyThreshold } from "./TrainWorthyThreshold";
import classNames from "classnames";

export default function WorkspaceSettingsPage() {
  const [activeModal, setActiveModal] = useState<UserModalType | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

  const navigate = useNavigate();
  const isUserManagementEnabled =
    useFeatureFlagService(USER_MANAGEMENT).enabled;
  const currentUserId = useSelector(getUserInfo).userId;
  const {
    canManageWorkspace,
    canViewWorkspace,
    isLoading: configLoading,
  } = useUserWorkspace();

  const { loading: tableLoading, tableData, fetchListData } = useUserTable();

  useEffect(() => {
    // Any workspace member can view the members list (read-only)
    if (isUserManagementEnabled && isOnPremUser() && canViewWorkspace) {
      fetchListData();
    }
  }, [isUserManagementEnabled, canViewWorkspace, fetchListData]);

  const handleClick = () => {
    setActiveModal(UserModalType.CREATE_USER);
    setSelectedUser(null);
  };

  const handleOpenModal = (type: UserModalType, user?: UserData) => {
    setActiveModal(type);
    if (user) {
      setSelectedUser(user);
    } else {
      setSelectedUser(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Workspace Settings</title>
      </Helmet>
      <ContainerHeaderLayout
        upperRowClassName={styles.customUpperRow}
        upperRow={
          <div className={styles.pageHeader}>
            <div className={styles.title}>
              <div className={styles.pageTitle}>Workspace Settings</div>
              <div className={styles.pageSubtitle}>
                {canManageWorkspace
                  ? "Manage your workspace and team members"
                  : "View workspace team members"}
              </div>
            </div>
            {/* Only show Create User button for admins */}
            {canManageWorkspace && (
              <div className={styles.createUser}>
                <VLButton
                  type="primary"
                  className={styles.createUserButton}
                  onClick={handleClick}
                >
                  Create User
                </VLButton>
              </div>
            )}
          </div>
        }
      />

      {configLoading ? (
        <VLColorfulLoader />
      ) : isUserManagementEnabled && isOnPremUser() && canViewWorkspace ? (
        <div className={styles.content}>
          {/* Only show TrainWorthyThreshold for admins */}
          {canManageWorkspace && <TrainWorthyThreshold />}
          <UsersTable
            openModal={handleOpenModal}
            loading={tableLoading}
            tableData={tableData}
            currentUserId={currentUserId}
            canManage={canManageWorkspace}
            className={classNames({
              [styles.withThreshold]: canManageWorkspace,
            })}
          />
          {/* Only render modals for admins */}
          {canManageWorkspace && (
            <UserModalManager
              activeModal={activeModal}
              onClose={() => setActiveModal(null)}
              selectedUser={selectedUser}
              onRefresh={fetchListData}
            />
          )}
        </div>
      ) : (
        <UnavailableModal
          onClose={() => navigate("/datasets")}
          actionText={"Go back to datasets page"}
        />
      )}
    </>
  );
}
